/* eslint-disable @typescript-eslint/naming-convention */

import {
  createOrUpdateSetting,
  exportTrimBim,
  getAlignment,
  getSettings,
  listAlignments,
  listRails,
  projectToStation,
  transformToPlotSet,
  type Alignment,
  type AlignmentSegmentTag,
  type ListAlignmentsResponse,
  type ListRailsResponse,
  type ProjectToStationResponse,
  type TransformToPlotSetResponse,
} from '../generated/index';
import { Client } from '@hey-api/client-fetch';
import { useQuery, UseQueryResult } from '@tanstack/solid-query';
import { Accessor } from 'solid-js';
import { returnOrThrow } from '@trimble/utilities';
import { Vector3 } from '@technology/web3d';
import { LengthUnit } from '@fulcrum/lib';
import { AlignmentSettings } from '@trimble/types';
import { getChainageUnit, trnToUUID } from '@trimble/utilities';

export type AlignmentCoordinates = {
  x: Array<number>;
  y: Array<number>;
};

/** hey-api path substitution does not encode; TRNs contain ':' and must be escaped. */
function encodeTenantTrnPathParam(tenantTrn: string): string {
  return encodeURIComponent(tenantTrn);
}

export class AlignmentApiService {
  /** Key used to store/retrieve alignment display settings via the Alignments API. */
  private static readonly ALIGNMENT_SETTINGS_KEY = 'NumarkAlignmentSettings';

  /** HTTP 404 – the resource was not found. */
  // eslint-disable-next-line no-magic-numbers -- well-known HTTP status code
  private static readonly HTTP_NOT_FOUND = 404;

  constructor(private readonly client: Client) {}

  /**
   * Retrieve alignment display settings from the Alignments API.
   *
   * Returns the parsed settings object, or `null` when the setting does not
   * exist (404) or the stored value is corrupted / not valid JSON.
   *
   * **Throws** on transient failures (e.g. 500, network errors) so that
   * callers can distinguish "no settings saved yet" from "we couldn't reach
   * the server" and avoid accidentally overwriting real data with defaults.
   */
  public async getAlignmentSettings(
    tenantTrn: string
  ): Promise<Partial<AlignmentSettings> | null> {
    const response = await getSettings({
      path: {
        tenantTrn: encodeTenantTrnPathParam(tenantTrn),
        key: AlignmentApiService.ALIGNMENT_SETTINGS_KEY,
      },
      client: this.client,
    });

    // A 404 means the setting has never been created — return null so the
    // caller can initialise defaults.  Any other HTTP error is treated as
    // transient and propagated so the caller does NOT blindly overwrite
    // previously-saved settings with defaults.
    if (response.response?.status === AlignmentApiService.HTTP_NOT_FOUND) {
      return null;
    }

    const setting = returnOrThrow(
      response,
      'Error while getting alignment settings'
    );

    if (setting.value === undefined || setting.value === null) {
      return null;
    }

    // The value is stored as a JSON string.  Depending on the API
    // serialisation it may come back as a string or as an already-parsed
    // object.
    try {
      const parsed =
        typeof setting.value === 'string'
          ? JSON.parse(setting.value)
          : setting.value;

      if (typeof parsed !== 'object' || parsed === null) {
        return null;
      }

      return parsed as Partial<AlignmentSettings>;
    } catch {
      // Corrupted stored value — treat as "no settings".
      return null;
    }
  }

  /**
   * Persist alignment display settings to the Alignments API.
   *
   * Throws on failure — callers are expected to handle the error.
   */
  public async saveAlignmentSettings(
    tenantTrn: string,
    settings: AlignmentSettings
  ): Promise<void> {
    const response = await createOrUpdateSetting({
      path: {
        tenantTrn: encodeTenantTrnPathParam(tenantTrn),
        key: AlignmentApiService.ALIGNMENT_SETTINGS_KEY,
      },
      body: {
        value: JSON.stringify(settings),
      },
      client: this.client,
    });

    returnOrThrow(response, 'Error while saving alignment settings');
  }

  public listAlignmentsQuery(
    tenantTrn: Accessor<string | null | undefined>
  ): UseQueryResult<ListAlignmentsResponse, Error> {
    return useQuery(() => ({
      queryKey: ['alignments', tenantTrn()],
      queryFn: async () => {
        // TODO: replace hardcoded region with actual region
        const response = await listAlignments({
          path: {
            tenantTrn: encodeTenantTrnPathParam(tenantTrn()!),
          },
          client: this.client,
        });

        return returnOrThrow(response, 'Error while listing alignments');
      },
      enabled: tenantTrn() != undefined,
    }));
  }

  public listRailsQuery(
    tenantTrn: Accessor<string | null | undefined>
  ): UseQueryResult<ListRailsResponse, Error> {
    return useQuery(() => ({
      queryKey: ['rails', tenantTrn()],
      queryFn: async () => {
        const response = await listRails({
          path: { tenantTrn: encodeTenantTrnPathParam(tenantTrn()!) },
          client: this.client,
        });
        const rails = returnOrThrow(response, 'Error while listing rails');
        return rails.map((rail) => ({
          ...rail,
          id: rail.id && trnToUUID(rail.id),
        }));
      },
      enabled: tenantTrn() != undefined,
    }));
  }

  private sanitizeLengthUnitAndDecimals(
    lengthUnit: LengthUnit,
    lengthDecimals: number
  ): { sanitizedLengthUnit: string; sanitizedLengthDecimals: number } {
    const sanitizedLengthDecimals = lengthDecimals < 0 ? 0 : lengthDecimals;
    const sanitizedLengthUnit = getChainageUnit(lengthUnit);
    return { sanitizedLengthUnit, sanitizedLengthDecimals };
  }

  public async exportMissionPathTrimbim(
    tenantTrn: string,
    alignmentId: string,
    segmentTags: AlignmentSegmentTag[],
    lengthUnit: LengthUnit,
    lengthDecimals: number,
    signal: AbortSignal,
    stationLabelSpacing?: number,
    colorName?: string
  ): Promise<ArrayBuffer> {
    const { sanitizedLengthUnit, sanitizedLengthDecimals } =
      this.sanitizeLengthUnitAndDecimals(lengthUnit, lengthDecimals);
    const hasExportSettings =
      colorName !== undefined || stationLabelSpacing !== undefined;
    const exportTrimBimRequest = {
      path: {
        id: alignmentId,
        tenantTrn: encodeTenantTrnPathParam(tenantTrn),
      },
      body: {
        segmentTags,
        ...(hasExportSettings && {
          exportSettings: {
            ...(colorName !== undefined && { colorName }),
            ...(stationLabelSpacing !== undefined && { stationLabelSpacing }),
          },
        }),
        uomPreference: {
          linearUnit: sanitizedLengthUnit,
          linearDecimals: sanitizedLengthDecimals,
        },
      },
    };
    const response = await exportTrimBim({
      ...exportTrimBimRequest,
      client: this.client,
      parseAs: 'arrayBuffer',
      signal,
    });

    return returnOrThrow(
      response,
      'Error while getting TrimBim display model'
    ) as unknown as ArrayBuffer;
  }

  public async getAlignment(
    tenantTrn: string,
    alignmentId: string
  ): Promise<Alignment> {
    const response = await getAlignment({
      path: {
        id: alignmentId,
        tenantTrn: encodeTenantTrnPathParam(tenantTrn),
      },
      query: {
        LinearDecimals: 3,
        LinearUnit: 'm',
      },
      client: this.client,
    });

    return returnOrThrow(response, 'Error while getting an alignment');
  }

  public *createAlignmentCoordinateGenerator(
    tenantTrn: string,
    alignmentId: string,
    beginStation: number,
    endStation: number,
    segmentLength: number,
    numberOfSegmentsPerRequest: number
  ): Generator<Promise<AlignmentCoordinates>, void, void> {
    let currentStationStart = beginStation;

    while (currentStationStart < endStation) {
      const currentStationEnd = Math.min(
        currentStationStart + segmentLength * numberOfSegmentsPerRequest,
        endStation
      );

      yield this.getAlignmentCoordinates(
        tenantTrn,
        alignmentId,
        currentStationStart,
        currentStationEnd,
        segmentLength
      );

      currentStationStart = currentStationEnd + segmentLength;
    }
  }

  public async projectCoordinateToAlignment(
    tenantTrn: string,
    alignmentId: string,
    point: Vector3,
    useCant?: boolean,
    useGradient?: boolean
  ): Promise<ProjectToStationResponse> {
    const response = await projectToStation({
      path: {
        id: alignmentId,
        tenantTrn: encodeTenantTrnPathParam(tenantTrn),
      },
      body: {
        x: point.x,
        y: point.y,
        z: point.z,
      },
      query: {
        LinearDecimals: 3,
        LinearUnit: 'm',
        UseCant: useCant,
        UseGradient: useGradient,
      },
      client: this.client,
    });

    const responseData = returnOrThrow(
      response,
      'Error while projecting coordinate to alignment'
    );

    return responseData;
  }

  private async getAlignmentCoordinates(
    tenantTrn: string,
    alignmentId: string,
    beginStation: number,
    endStation: number,
    segmentLength: number
  ): Promise<AlignmentCoordinates> {
    const response = await transformToPlotSet({
      path: {
        id: alignmentId,
        tenantTrn: encodeTenantTrnPathParam(tenantTrn),
      },
      body: {
        beginStation,
        endStation,
        segmentLength,
        offset: 0,
        height: 0,
      },
      query: {
        LinearDecimals: 3,
        LinearUnit: 'm',
      },
      client: this.client,
    });

    const responseData = returnOrThrow(
      response,
      'Error while getting alignment coordinates'
    );

    return this.convertAlignmentPlotsetToCoordinates(responseData);
  }

  private convertAlignmentPlotsetToCoordinates(
    plotset: TransformToPlotSetResponse
  ): AlignmentCoordinates {
    // get indices of x and y components
    let xIdx = -1;
    let yIdx = -1;

    plotset.components!.forEach((component, idx) => {
      // the first component is the xAxis station,
      // which is not in the api schema, nor in the components list for some reason
      // so we add +1 to the index
      if (component.name === 'X') {
        xIdx = idx + 1;
      } else if (component.name === 'Y') {
        yIdx = idx + 1;
      }
    });

    if (xIdx === -1 || yIdx === -1) {
      throw new Error('X or Y component was not found');
    }

    // extract x and y coordinates
    return <AlignmentCoordinates>{
      x: plotset.values!.map((value) => value[xIdx]),
      y: plotset.values!.map((value) => value[yIdx]),
    };
  }
}
